<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  wizardData: { type: Object, required: true },
});

const emit = defineEmits(['update', 'next']);
const { t } = useI18n();

const labels = useMapGetter('labels/getLabels');

const audienceList = computed(
  () => labels.value?.map(l => ({ value: l.id, label: l.title })) ?? []
);

const isLabel = computed(() => props.wizardData.audienceType === 'label');
const isCsv = computed(() => props.wizardData.audienceType === 'csv_import');

const showErrors = ref(false);

const isValid = computed(() => {
  if (isCsv.value) return true;
  return props.wizardData.selectedAudience.length > 0;
});

const validate = () => {
  showErrors.value = true;
  return isValid.value;
};

defineExpose({ validate });

const selectAudienceType = type => {
  emit('update', {
    audienceType: type,
    selectedAudience: [],
    importId: null,
    csvHeaders: [],
    csvSampleRows: [],
    csvTotalRows: 0,
    columnMapping: {},
  });
  if (type === 'csv_import') {
    emit('next');
  }
};

const updateAudience = value => {
  emit('update', { selectedAudience: value });
};

const updateExcludedLabels = value => {
  emit('update', { excludedLabels: value });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.TITLE') }}
    </h3>
    <p class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.DESCRIPTION') }}
    </p>

    <!-- Audience type toggle -->
    <div class="flex gap-3">
      <button
        type="button"
        class="flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors cursor-pointer"
        :class="
          isLabel
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak hover:border-n-slate-6'
        "
        @click="selectAudienceType('label')"
      >
        <span class="i-lucide-tags size-6 text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.BY_LABELS') }}
        </span>
        <span class="text-xs text-n-slate-10 text-center">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.LABELS_DESC') }}
        </span>
      </button>

      <button
        type="button"
        class="flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors cursor-pointer"
        :class="
          isCsv
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak hover:border-n-slate-6'
        "
        @click="selectAudienceType('csv_import')"
      >
        <span class="i-lucide-file-spreadsheet size-6 text-n-slate-11" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.BY_CSV') }}
        </span>
        <span class="text-xs text-n-slate-10 text-center">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.CSV_DESC') }}
        </span>
      </button>
    </div>

    <!-- Labels selector (when label audience selected) -->
    <div v-if="isLabel" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        :model-value="wizardData.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="showErrors && wizardData.selectedAudience.length === 0"
        :message="
          showErrors && wizardData.selectedAudience.length === 0
            ? t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.LABELS_REQUIRED')
            : ''
        "
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="updateAudience"
      />
    </div>

    <!-- Exclusion labels (only for label audience — CSV has its own in StepCsvUpload) -->
    <div v-if="isLabel" class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_LABEL') }}
      </label>
      <TagMultiSelectComboBox
        :model-value="wizardData.excludedLabels"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_LABEL')"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_PLACEHOLDER')
        "
        class="[&>div>button]:bg-n-alpha-black2"
        @update:model-value="updateExcludedLabels"
      />
    </div>
  </div>
</template>
